import { createElement, Fragment } from "@wordpress/element";

/**
 * WordPress dependencies
 */
import { useEntityProp, store as coreStore } from '@wordpress/core-data';
import { useSelect, useDispatch } from '@wordpress/data';
import { MenuItem, ToggleControl, PanelBody, Placeholder, Button } from '@wordpress/components';
import { InspectorControls, BlockControls, MediaPlaceholder, MediaReplaceFlow, useBlockProps, store as blockEditorStore } from '@wordpress/block-editor';
import { __, sprintf } from '@wordpress/i18n';
import { upload } from '@wordpress/icons';
import { SVG, Path } from '@wordpress/primitives';
import { store as noticesStore } from '@wordpress/notices';
/**
 * Internal dependencies
 */

import DimensionControls from './dimension-controls';
const placeholderIllustration = createElement(SVG, {
  className: "components-placeholder__illustration",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  viewBox: "0 0 60 60",
  preserveAspectRatio: "none"
}, createElement(Path, {
  vectorEffect: "non-scaling-stroke",
  d: "M60 60 0 0"
}));
const ALLOWED_MEDIA_TYPES = ['image'];
const placeholderChip = createElement("div", {
  className: "wp-block-post-featured-image__placeholder"
}, placeholderIllustration);

function getMediaSourceUrlBySizeSlug(media, slug) {
  var _media$media_details, _media$media_details$, _media$media_details$2;

  return (media === null || media === void 0 ? void 0 : (_media$media_details = media.media_details) === null || _media$media_details === void 0 ? void 0 : (_media$media_details$ = _media$media_details.sizes) === null || _media$media_details$ === void 0 ? void 0 : (_media$media_details$2 = _media$media_details$[slug]) === null || _media$media_details$2 === void 0 ? void 0 : _media$media_details$2.source_url) || (media === null || media === void 0 ? void 0 : media.source_url);
}

function PostFeaturedImageDisplay(_ref) {
  let {
    clientId,
    attributes,
    setAttributes,
    context: {
      postId,
      postType: postTypeSlug,
      queryId
    }
  } = _ref;
  const isDescendentOfQueryLoop = Number.isFinite(queryId);
  const {
    isLink,
    height,
    width,
    scale,
    sizeSlug
  } = attributes;
  const [featuredImage, setFeaturedImage] = useEntityProp('postType', postTypeSlug, 'featured_media', postId);
  const {
    media,
    postType
  } = useSelect(select => {
    const {
      getMedia,
      getPostType
    } = select(coreStore);
    return {
      media: featuredImage && getMedia(featuredImage, {
        context: 'view'
      }),
      postType: postTypeSlug && getPostType(postTypeSlug)
    };
  }, [featuredImage, postTypeSlug]);
  const mediaUrl = getMediaSourceUrlBySizeSlug(media, sizeSlug);
  const imageSizes = useSelect(select => select(blockEditorStore).getSettings().imageSizes, []);
  const imageSizeOptions = imageSizes.filter(_ref2 => {
    var _media$media_details2, _media$media_details3, _media$media_details4;

    let {
      slug
    } = _ref2;
    return media === null || media === void 0 ? void 0 : (_media$media_details2 = media.media_details) === null || _media$media_details2 === void 0 ? void 0 : (_media$media_details3 = _media$media_details2.sizes) === null || _media$media_details3 === void 0 ? void 0 : (_media$media_details4 = _media$media_details3[slug]) === null || _media$media_details4 === void 0 ? void 0 : _media$media_details4.source_url;
  }).map(_ref3 => {
    let {
      name,
      slug
    } = _ref3;
    return {
      value: slug,
      label: name
    };
  });
  const blockProps = useBlockProps({
    style: {
      width,
      height
    }
  });

  const placeholder = content => {
    return createElement(Placeholder, {
      className: "block-editor-media-placeholder"
    }, placeholderIllustration, content);
  };

  const onSelectImage = value => {
    if (value !== null && value !== void 0 && value.id) {
      setFeaturedImage(value.id);
    }
  };

  const {
    createErrorNotice
  } = useDispatch(noticesStore);

  const onUploadError = message => {
    createErrorNotice(message, {
      type: 'snackbar'
    });
  };

  const controls = createElement(Fragment, null, createElement(DimensionControls, {
    clientId: clientId,
    attributes: attributes,
    setAttributes: setAttributes,
    imageSizeOptions: imageSizeOptions
  }), createElement(InspectorControls, null, createElement(PanelBody, {
    title: __('Link settings')
  }, createElement(ToggleControl, {
    label: postType !== null && postType !== void 0 && postType.labels.singular_name ? sprintf( // translators: %s: Name of the post type e.g: "post".
    __('Link to %s'), postType.labels.singular_name.toLowerCase()) : __('Link to post'),
    onChange: () => setAttributes({
      isLink: !isLink
    }),
    checked: isLink
  }))));
  let image;

  if (!featuredImage && isDescendentOfQueryLoop) {
    return createElement(Fragment, null, controls, createElement("div", blockProps, placeholderChip));
  }

  const label = __('Add a featured image');

  if (!featuredImage) {
    image = createElement(MediaPlaceholder, {
      onSelect: onSelectImage,
      accept: "image/*",
      allowedTypes: ALLOWED_MEDIA_TYPES,
      onError: onUploadError,
      placeholder: placeholder,
      mediaLibraryButton: _ref4 => {
        let {
          open
        } = _ref4;
        return createElement(Button, {
          icon: upload,
          variant: "primary",
          label: label,
          showTooltip: true,
          tooltipPosition: "top center",
          onClick: () => {
            open();
          }
        });
      }
    });
  } else {
    // We have a Featured image so show a Placeholder if is loading.
    image = !media ? placeholderChip : createElement("img", {
      src: mediaUrl,
      alt: media.alt_text ? sprintf( // translators: %s: The image's alt text.
      __('Featured image: %s'), media.alt_text) : __('Featured image'),
      style: {
        height,
        objectFit: height && scale
      }
    });
  }

  return createElement(Fragment, null, controls, !!media && !isDescendentOfQueryLoop && createElement(BlockControls, {
    group: "other"
  }, createElement(MediaReplaceFlow, {
    mediaId: featuredImage,
    mediaURL: mediaUrl,
    allowedTypes: ALLOWED_MEDIA_TYPES,
    accept: "image/*",
    onSelect: onSelectImage,
    onError: onUploadError
  }, createElement(MenuItem, {
    onClick: () => setFeaturedImage(0)
  }, __('Reset')))), createElement("figure", blockProps, image));
}

export default function PostFeaturedImageEdit(props) {
  var _props$context;

  const blockProps = useBlockProps();

  if (!((_props$context = props.context) !== null && _props$context !== void 0 && _props$context.postId)) {
    return createElement("div", blockProps, placeholderChip);
  }

  return createElement(PostFeaturedImageDisplay, props);
}
//# sourceMappingURL=edit.js.map